// src/pages/ChannelsPage.js

import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { db } from '../firebase'
import {
  collection,
  onSnapshot,
  query,
  where,
  orderBy,
  doc,
  getDoc,
  setDoc,
  serverTimestamp
} from 'firebase/firestore'
import {
  Container,
  Grid,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Box,
  IconButton,
  Tooltip,
  Badge,
  Snackbar,
  Button
} from '@mui/material'
import GroupsIcon from '@mui/icons-material/Groups'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ForumIcon from '@mui/icons-material/Forum'
import PersonIcon from '@mui/icons-material/Person'
import SendIcon from '@mui/icons-material/Send'

import ChannelList from '../components/ChannelList'
import ChannelChat from '../components/ChannelChat'
import UserList from '../components/UserList'
import PrivateChat from '../components/PrivateChat'

// Çoklu dil
import { useTranslation } from 'react-i18next'

export default function ChannelsPage() {
  const { t } = useTranslation() // i18n Hook

  const { workspaceId } = useParams()
  const { currentUser } = useAuth()

  const [selectedChannel, setSelectedChannel] = useState(null)
  const [selectedUser, setSelectedUser] = useState(null)
  const [userRole, setUserRole] = useState(null)

  const [notifications, setNotifications] = useState([])
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')

  // lastRead'ı güncelleme fonksiyonu
  const updateLastRead = async (chatId) => {
    if (!currentUser) return
    const statusRef = doc(db, 'userChatStatus', `${currentUser.uid}_${chatId}`)
    await setDoc(statusRef, { lastRead: serverTimestamp() }, { merge: true })
  }

  // Kanal seçimi
  const handleChannelSelect = (channel) => {
    setSelectedChannel(channel)
    setSelectedUser(null)

    const currentMember = channel.memberDetails.find(
      (member) => member.userId === currentUser.uid
    )
    if (currentMember) {
      setUserRole(currentMember.role)
    } else {
      setUserRole(null)
    }

    if (channel.id) {
      setNotifications((prev) => prev.filter((msg) => msg.channelId !== channel.id))
      updateLastRead(channel.id) // lastRead'ı güncelle
    }
  }

  // Kullanıcı seçimi
  const handleUserSelect = (user) => {
    if (user.email !== currentUser.email) {
      if (userRole === 'observer' || userRole === 'user') {
        if (user.role !== 'moderator') {
          alert(t('channelsPage.alertOnlyModerator'))
          return
        }
      }
      setSelectedUser(user)

      const chatId = [currentUser.uid, user.uid].sort().join('_')
      setNotifications((prev) => prev.filter((msg) => msg.chatId !== chatId))
      updateLastRead(chatId) // lastRead'ı güncelle
    }
  }

  // Özel sohbetten geri dönme
  const handleReturnToChannel = () => {
    setSelectedUser(null)
  }

  // Bildirimleri dinlemek için useEffect
  useEffect(() => {
    if (!currentUser) return

    const privateMessagesRef = collection(db, 'privateMessages')
    const q = query(
      privateMessagesRef,
      where('participants', 'array-contains', currentUser.uid),
      where('userId', '!=', currentUser.uid),
      orderBy('createdAt', 'desc')
    )

    const unsubscribe = onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach(async (change) => {
        if (change.type === 'added') {
          const message = { id: change.doc.id, ...change.doc.data() }

          const chatId = message.chatId

          // lastRead'ı al
          const statusRef = doc(db, 'userChatStatus', `${currentUser.uid}_${chatId}`)
          const statusSnap = await getDoc(statusRef)
          const lastRead = statusSnap.exists() ? statusSnap.data().lastRead : null

          // Eğer lastRead yoksa veya mesaj createdAt'den sonra ise bildir
          if (
            !lastRead ||
            (message.createdAt &&
              message.createdAt.toMillis() > lastRead.toMillis())
          ) {
            // Eğer kullanıcı şu anda bu mesajın bulunduğu sohbeti görüntülemiyorsa bildir
            const isCurrentChat =
              selectedUser &&
              [selectedUser.uid, currentUser.uid].sort().join('_') === chatId

            if (!isCurrentChat) {
              // Bildirimi ekle
              setNotifications((prev) => [...prev, message])

              // Snackbar mesajını ayarla
              const otherUserId = message.participants.find(
                (uid) => uid !== currentUser.uid
              )
              const userSnap = await getDoc(doc(db, 'users', otherUserId))
              const otherUser = userSnap.exists()
                ? userSnap.data()
                : { email: t('channelsPage.unknownUser') }

              // i18n interpolation örneği
              setSnackbarMessage(
                t('channelsPage.snackbarMessage', {
                  senderName: otherUser.nickname || otherUser.email
                })
              )
              setSnackbarOpen(true)
            }
          }
        }
      })
    })

    return () => unsubscribe()
  }, [currentUser, selectedUser, t])

  // Snackbar kapatma işlemi
  const handleSnackbarClose = () => {
    setSnackbarOpen(false)
  }

  // Bildirime tıklama işlemi
  const handleNotificationClick = async () => {
    if (notifications.length === 0) return

    const firstMessage = notifications[0]
    const otherUserId = firstMessage.participants.find(
      (uid) => uid !== currentUser.uid
    )
    const chatId = [currentUser.uid, otherUserId].sort().join('_')

    try {
      const userRef = doc(db, 'users', otherUserId)
      const userSnap = await getDoc(userRef)
      if (userSnap.exists()) {
        const userData = { id: userSnap.id, ...userSnap.data() }
        setSelectedUser(userData)
        // Bildirimleri temizle ilgili sohbetle ilgili olanları
        setNotifications((prev) => prev.filter((msg) => msg.chatId !== chatId))
        setSnackbarOpen(false)
      }
    } catch (error) {
      console.error('Kullanıcı alınırken hata:', error)
    }
  }

  return (
    <Container
      sx={{
        mt: 4,
        backgroundColor: '#f9f9f9',
        padding: 4,
        borderRadius: 2
      }}
    >
      {/* Workspace Başlığı */}
      <Typography variant="h4" gutterBottom align="center" sx={{ color: '#3f51b5' }}>
        <GroupsIcon sx={{ mr: 1 }} />
        {t('channelsPage.workspaceTitle')}
      </Typography>

      {/* Bildirim Badge'ı */}
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Tooltip title={t('channelsPage.notificationTitle')}>
          <IconButton color="primary" onClick={handleNotificationClick}>
            <Badge badgeContent={notifications.length} color="secondary">
              <SendIcon />
            </Badge>
          </IconButton>
        </Tooltip>
      </Box>

      {/* Grid layout */}
      <Grid container spacing={3}>
        {/* Sol Kısım: Kanal Listesi & Kullanıcı Listesi */}
        <Grid item xs={12} md={3}>
          {/* Kanallar Kartı */}
          <Card variant="outlined">
            <CardHeader
              title={t('channelsPage.channelCardTitle')}
              avatar={<ForumIcon color="secondary" />}
              action={
                <Tooltip title={t('channelsPage.newChannelTooltip')}>
                  <IconButton aria-label="add channel">
                    {/* Yeni Kanal Oluşturma Butonu (opsiyonel) */}
                  </IconButton>
                </Tooltip>
              }
            />
            <Divider />
            <CardContent>
              <ChannelList
                workspaceId={workspaceId}
                onChannelSelect={handleChannelSelect}
                selectedChannel={selectedChannel}
              />
            </CardContent>
          </Card>

          {/* Kullanıcılar Kartı */}
          <Card variant="outlined" sx={{ mt: 4 }}>
            <CardHeader
              title={t('channelsPage.userCardTitle')}
              avatar={<PersonIcon color="primary" />}
              action={
                <Tooltip title={t('channelsPage.newUserTooltip')}>
                  <IconButton aria-label="add user">
                    {/* Yeni Kullanıcı Ekleme Butonu (opsiyonel) */}
                  </IconButton>
                </Tooltip>
              }
            />
            <Divider />
            <CardContent>
              {selectedChannel ? (
                <UserList
                  selectedChannel={selectedChannel}
                  onUserSelect={handleUserSelect}
                  userRole={userRole}
                />
              ) : (
                <Typography color="textSecondary">
                  {t('channelsPage.userCardHint')}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Sağ Kısım: Kanal veya Özel Sohbet */}
        <Grid item xs={12} md={9}>
          {selectedUser ? (
            <Card
              variant="outlined"
              sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
            >
              <CardHeader
                avatar={<PersonIcon color="primary" />}
                title={`${selectedUser.nickname || selectedUser.email}${t('channelsPage.userChatTitle')}`}
                action={
                  <Tooltip title={t('channelsPage.returnToChannelTooltip')}>
                    <IconButton onClick={handleReturnToChannel}>
                      <ArrowBackIcon />
                    </IconButton>
                  </Tooltip>
                }
              />
              <Divider />
              <CardContent sx={{ flexGrow: 1 }}>
                <PrivateChat selectedUser={selectedUser} userRole={userRole} />
              </CardContent>
            </Card>
          ) : selectedChannel ? (
            <Card
              variant="outlined"
              sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
            >
              <CardHeader
                avatar={<ForumIcon color="secondary" />}
                title={`${selectedChannel.name} ${t('channelsPage.oneChannelTitleSuffix')}`}
              />
              <Divider />
              <CardContent sx={{ flexGrow: 1 }}>
                <ChannelChat channel={selectedChannel} userRole={userRole} />
              </CardContent>
            </Card>
          ) : (
            <Card
              variant="outlined"
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <CardContent>
                <Typography variant="h6" color="textSecondary">
                  {t('channelsPage.selectChannelPlaceholder')}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Bildirim Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        action={
          <Button color="secondary" size="small" onClick={handleNotificationClick}>
            {t('channelsPage.seeNotification')}
          </Button>
        }
      />
    </Container>
  )
}